import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Button, Modal, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import DynamicFormSP from './DynamicFormSP.component';
import { fieldEntityActionConfig } from './utils/fieldEntityActionConfig';

const { Text } = Typography;

const SingleEntitySelect = ({
  singularEntity = '',
  pluralEntity = '',
  constantPrefix = '',
  listEntities,
  onChange,
  value,
  primarySearchProp = '',
  secondarySearchProp = '',
  enableCreate = true,
  fieldsToHide = ['Id'],
  /** Redux slice key. Defaults to `${singularEntity}List` — override to avoid clobbering a shared list. */
  listStateKey = null,
  placeholder = null,
  style = { width: '100%' },
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [entityListFilter, setEntityListFilter] = useState({
    query: '',
    page: 1,
    limit: 10,
    filter: primarySearchProp || '',
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState(null);

  // Safely capitalize the entity name
  const capitalizedSingularEntity = singularEntity ? singularEntity.charAt(0).toUpperCase() + singularEntity.slice(1) : '';
  const singularEntityLabel = singularEntity ? t(singularEntity) : '';
  const entityConfig = capitalizedSingularEntity ? 
    (fieldEntityActionConfig[`${capitalizedSingularEntity}Ids`] || fieldEntityActionConfig[`${capitalizedSingularEntity}Id`]) 
    : null;

  const resolvedListKey = listStateKey || `${singularEntity}List`;
  const entityList = useSelector((state) => state[resolvedListKey] || {});
  const { loading: loadingList, [pluralEntity]: entities = [] } = entityList;
  const entityCreate = useSelector((state) => state[`${singularEntity}Create`] || {});
  const { loading: loadingCreate, success: createSuccess, error: createError } = entityCreate;

  useEffect(() => {
    if (listEntities && dispatch) {
      try {
        dispatch(listEntities(entityListFilter));
      } catch (err) {
        setError(t('Failed to load entities'));
        console.error('Error loading entities:', err);
      }
    }
  }, [dispatch, listEntities, entityListFilter]);

  useEffect(() => {
    if (!isModalVisible) return;

    if (createSuccess) {
      setIsModalVisible(false);
      setError(null);
      dispatch(listEntities(entityListFilter));
      return;
    }

    if (createError) {
      const errorMessage = Array.isArray(createError) ? createError.join(', ') : createError;
      setError(errorMessage || t('Failed to create entity'));
    }
  }, [createSuccess, createError, isModalVisible, dispatch, listEntities, entityListFilter, t]);

  const debouncedSearch = debounce((searchValue) => {
    setEntityListFilter((prevFilter) => ({
      ...prevFilter,
      query: searchValue || '',
    }));
  }, 300);

  const showModal = () => {
    setIsDropdownOpen(false);
    const createResetType = `${(entityConfig?.constantPrefix || constantPrefix || '').toUpperCase()}_CREATE_RESET`;
    if (createResetType !== '_CREATE_RESET') {
      dispatch({ type: createResetType });
    }
    setError(null);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleCreate = (newEntity) => {
    if (entityConfig?.createEntity && dispatch) {
      try {
        setError(null);
        dispatch(entityConfig.createEntity(newEntity));
      } catch (err) {
        setError(t('Failed to create entity'));
        console.error('Error creating entity:', err);
      }
    }
  };

  const dropdownRender = (menu) => (
    <div>
      {menu}
      {enableCreate && entityConfig && (
        <div
          style={{
            padding: '8px',
            borderTop: '1px solid #e8e8e8',
          }}
        >
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={showModal}
            style={{ width: '100%', textAlign: 'left' }}
          >
            {t('Create New')} {singularEntityLabel}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Select
        value={value}
        onChange={(val, option) => {
          // Only use the primary value for the label
          const primaryValue = entities.find(e => e.id === val)?.[primarySearchProp] || '';
          onChange?.(val, { value: val, label: primaryValue });
        }}
        loading={loadingList}
        style={style}
        allowClear
        showSearch
        filterOption={false}
        onSearch={debouncedSearch}
        placeholder={placeholder || `${t('Select')} ${singularEntityLabel}`}
        dropdownRender={dropdownRender}
        open={isDropdownOpen}
        onDropdownVisibleChange={setIsDropdownOpen}
        notFoundContent={loadingList ? <Spin size="small" /> : null}
      >
        {Array.isArray(entities) &&
          entities.map((entity) => {
            const primaryValue = entity?.[primarySearchProp] || '';
            const secondaryValue = entity?.[secondarySearchProp] ? ` | ${entity[secondarySearchProp]}` : '';
            return (
              <Select.Option
                key={entity?.id}
                value={entity?.id}
                data-primary-value={primaryValue}
              >
                {primaryValue}{secondaryValue}
              </Select.Option>
            );
          })}
      </Select>
      {error && !isModalVisible && <Text type="danger" style={{ display: 'block', marginTop: 8 }}>{error}</Text>}

      {entityConfig && (
        <Modal
          title={`${t('Create New')} ${singularEntityLabel}`}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width="80%"
          destroyOnClose
        >
          {error && <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>{error}</Text>}
          <DynamicFormSP
            singularEntity={singularEntity}
            pluralEntity={pluralEntity}
            constantPrefix={constantPrefix}
            loadDto={entityConfig.loadDto}
            createEntity={handleCreate}
            updateEntity={entityConfig.updateEntity}
            deleteEntity={entityConfig.deleteEntity}
            getEntityDetails={entityConfig.getEntityDetails}
            listEntities={entityConfig.listEntities}
            listEntitiesByIds={entityConfig.listEntitiesByIds}
            defaultEditMode="Create"
            fieldsToHide={fieldsToHide}
            isParent={false}
            enableCreate={true}
            enableSearchBar={false}
            enableActionButtons={false}
            saveLoading={loadingCreate}
          />
        </Modal>
      )}
    </>
  );
};

export default SingleEntitySelect;